/**
 * Every member's role, scope and schedule, as one shared fetch.
 *
 * Shared for the same reason as the users list: the Users list annotates each
 * row with the member's role while the details panel edits it, so a save that
 * refreshed only the editor left the list showing the old role.
 *
 * `canManage` is derived from the response rather than from a role carried in
 * the session. GET /members/permissions is admin-only, so a 403 IS the
 * capability answer — "you may not manage members" — and using it directly
 * means the UI cannot disagree with the server about what the caller may do.
 */

import { snapshotAtoms } from '../snapshotAtoms';
import { api } from '@/api';
import { ApiError } from '@/api/client';
import { HTTP_STATUS } from '@/api/httpStatus';
import { decodeSchedule } from '@/lib/schedule';
import type { MemberPermission, MemberPermissionsSnapshot } from '@/types/auth';

const EMPTY: MemberPermissionsSnapshot = {
    byEmail: {}, canManage: false, isLoading: false, error: null,
};
const PENDING: MemberPermissionsSnapshot = { ...EMPTY, isLoading: true };

const permissions = snapshotAtoms<MemberPermissionsSnapshot>(PENDING, async () => {
    try {
        const rows = await api.listMemberPermissions();
        const byEmail = rows.reduce<Record<string, MemberPermission>>((acc, row) => ({
            ...acc,
            [row.email]: {
                email: row.email,
                role: row.role,
                scopeId: row.scopeId,
                schedule: decodeSchedule(row.schedule),
            },
        }), {});
        return {
            byEmail, canManage: true, isLoading: false, error: null,
        };
    } catch (error) {
        if (error instanceof ApiError && error.status === HTTP_STATUS.FORBIDDEN) {
            return EMPTY;
        }
        // Anything else is a real failure; snapshotAtoms reports it as `error`.
        throw error;
    }
});

export const memberPermissionsAtom = permissions.value;
export const refreshMemberPermissionsAtom = permissions.reload;
